import {
  DateRangePicker as BaseDateRangeInput,
  type DateRangePickerProps,
  type DateRangePickerValue,
} from '@mantine/dates';
import { useMergedRef } from '@mantine/hooks';
import { FormControl, FormHelperText, IconButton, InputLabel } from '@mui/material';
import { styled, alpha } from '@mui/material/styles';
import { dayjs } from '@shared/utils';
import { forwardRef, useRef } from 'react';

import 'dayjs/locale/th';
import { CalendarTodayIcon, CloseRoundedIcon } from '../../icons';
import { ColorPalette } from '../../theme';

type DateRangeInputProps = Omit<DateRangePickerProps, 'sx'> & {
  helperText?: string;
  defaultTime?: { hour?: number; minute?: number; second?: number };
  onClear?: DateRangePickerProps['onChange'];
};

const StyledInputLabel = styled(InputLabel)({
  position: 'absolute',
  top: 0,
  left: 0,
});

const StyledDateRangeInput = styled(BaseDateRangeInput)<
  Omit<DateRangePickerProps, 'sx'> & {
    color?: ColorPalette;
    error?: boolean;
  }
>(({ theme, color: colorProp, error: errorProp }) => {
  const color = (errorProp ? ColorPalette.ERROR : colorProp) ?? ColorPalette.PRIMARY;
  return {
    marginTop: theme.spacing(6),
    border: '1px solid',
    borderColor: errorProp ? theme.palette.error.main : theme.palette.grey['300'],
    borderRadius: theme.shape.rounded(1),
    '& .mantine-DateRangePicker-dropdown': {
      padding: theme.spacing(4),
      '.mantine-DateRangePicker-monthPickerControl, .mantine-DateRangePicker-yearPickerControl': {
        fontFamily: theme.typography.fontFamily,
      },
      '.mantine-DateRangePicker-monthPickerControlActive, .mantine-DateRangePicker-yearPickerControlActive':
        {
          backgroundColor: theme.palette.primary.main,
          color: theme.palette.text.primary,
          fontWeight: 600, // TODO: use theme.typography.fontWeightSemibold
        },
      '.mantine-DateRangePicker-calendarHeader': {
        marginBottom: theme.spacing(3.5),
        '.mantine-DateRangePicker-calendarHeaderLevel': {
          fontFamily: theme.typography.fontFamily,
          fontSize: theme.typography.body2.fontSize,
          height: 'auto',
          lineHeight: 1.5,
          fontWeight: 600, // TODO: use theme.typography.fontWeightSemibold
          '.mantine-DateRangePicker-calendarHeaderLevelIcon': {
            display: 'none',
          },
        },
      },
      '.mantine-DateRangePicker-month': {
        borderCollapse: 'separate',
        borderSpacing: '1px',
        '.mantine-DateRangePicker-calendarBase': {
          maxWidth: 'none',
        },
        '.mantine-DateRangePicker-weekday, .mantine-DateRangePicker-day': {
          fontFamily: theme.typography.fontFamily,
          fontSize: theme.typography.caption.fontSize,
          color: theme.palette.text.primary,
        },
        '.mantine-DateRangePicker-weekdayCell': {
          padding: theme.spacing(1, 2),
        },
        '.mantine-DateRangePicker-day': {
          width: '36px',
          height: '36px',
        },
        '[data-in-range="true"]': {
          backgroundColor: theme.palette.primary.lighter,
          color: theme.palette.text.primary,
        },
        '[data-selected="true"]': {
          backgroundColor: theme.palette.primary.main,
          color: theme.palette.text.primary,
          fontWeight: 600, // TODO: use theme.typography.fontWeightSemibold
        },
        '[data-weekend="true"]': {
          color: theme.palette.error.main,
        },
        '[disabled]': {
          color: theme.palette.text.disabled,
        },
      },
    },
    input: {
      fontFamily: theme.typography.fontFamily,
      color: theme.palette.text.primary,
      fontSize: theme.typography.fontSize,
      lineHeight: 1.5,
      fontWeight: theme.typography.fontWeightRegular,
      border: 'none',
      boxSizing: 'border-box',
      minHeight: '42px',
      padding: '16.5px 14px',
      height: '1.4375em',
      ':hover': { borderColor: theme.palette[color].main },
      ':focus': {
        borderColor: theme.palette[color].main,
        boxShadow: `${alpha(theme.palette[color].main, 0.25)} 0 0 0 3px`,
      },
      ':disabled': {
        opacity: 1,
        cursor: 'default',
        color: theme.palette.text.disabled,
        backgroundColor: '#F1F3F5',
        ':hover': { borderColor: theme.palette.grey['300'] },
        '::placeholder': { color: theme.palette.text.disabled },
      },
      textOverflow: 'ellipsis',
    },
  };
});

const DateRangeInput = forwardRef<HTMLInputElement, DateRangeInputProps>(
  (props: Readonly<DateRangeInputProps>, forwardedRef) => {
    const {
      label,
      value,
      onChange,
      onClear,
      helperText,
      error,
      color,
      clearable: isClearable = true,
      required: isRequired,
      fullWidth: isFullWidth = true,
      defaultTime,
      ...restProps
    } = props;

    const DateInputRef = useRef<HTMLInputElement>(null);
    const ref = useMergedRef(DateInputRef, forwardedRef);

    const {
      hour: hourProp = 23,
      minute: minuteProp = 59,
      second: secondProp = 59,
    } = defaultTime ?? {};

    const handleChange = (dateRangeValue: DateRangePickerValue) => {
      const dateRangeArrayWithChanges = dateRangeValue.map((dateValue) => {
        if (dayjs(dateValue).isValid()) {
          const hour = dayjs(dateValue).get('hour');
          const minute = dayjs(dateValue).get('minute');

          const dateWithChange = dateValue
            ? dayjs(dateValue).set('hour', hour).set('minute', minute).toDate()
            : dayjs(dateValue)
                .set('hour', hourProp)
                .set('minute', minuteProp)
                .set('second', secondProp)
                .toDate();
          return dateWithChange;
        } else {
          return dateValue;
        }
      }) as DateRangePickerValue;

      onChange?.(dateRangeArrayWithChanges);
    };

    const handleClear = () => {
      onClear?.([null, null]);
    };

    const rightButton =
      !isClearable || !value?.some(Boolean) ? (
        <CalendarTodayIcon sx={{ color: 'text.disabled', pointerEvents: 'none', fontSize: 24 }} />
      ) : (
        <IconButton
          onClick={handleClear}
          disabled={props.disabled}
          sx={{ color: 'text.secondary' }}
        >
          <CloseRoundedIcon />
        </IconButton>
      );

    return (
      <FormControl fullWidth={isFullWidth} required={isRequired}>
        {label ? <StyledInputLabel>{label}</StyledInputLabel> : null}
        <StyledDateRangeInput
          locale='th'
          ref={ref}
          value={value}
          onChange={handleChange}
          labelFormat='MMMM YYYY'
          inputFormat='D MMM YYYY'
          yearLabelFormat='YYYY'
          rightSection={rightButton}
          size='md'
          color={color as ColorPalette}
          firstDayOfWeek='sunday'
          error={!!error}
          allowSingleDateInRange
          {...restProps}
        />
        <FormHelperText error={!!error}>{helperText}</FormHelperText>
      </FormControl>
    );
  },
);

DateRangeInput.displayName = 'DateRangeInput';

export default DateRangeInput;
export type { DateRangeInputProps };
